import React, { createContext, useContext, useState, useEffect } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import CardModal from './CardModal.jsx';
import { cardBuilderActions } from './actions.mjs';
import CardsList from './CardsList.jsx';
import { cssBEM } from './constants';
import PropTypes from 'prop-types';
import { CardShape } from './shapes';

/**
 * @typedef CardBuilderContextObject
 * @property {string} builderKey - custom message key for builder
 * @property {Node} MessageComponent - component to use for CardBuilder messages
 * @type CardBuilderContextObject
 */
const defaultValue = {};
const CardBuilderContext = createContext(defaultValue);

const CardBuilder = (props) => {
  const {
    activeCardId,
    builderKey,
    cards,
    className,
    disabled,
    MessageComponent,
    onChange,
    showAllMode,
    ...rest
  } = props;

  useEffect(() => {
    if (disabled) {
      setShowCardOptions(false);
    }
  }, [disabled]);

  const activeCard = activeCardId
    ? cards.find(({ id }) => id === activeCardId)
    : false;
  const [showCardOptions, setShowCardOptions] = useState(false);
  const showModal = !disabled && (showCardOptions || activeCard);
  const classes = classnames(cssBEM, className, {
    [`${cssBEM}--modal-active`]: showModal,
    [`${cssBEM}--showAllMode`]: showAllMode,
  });

  const addCard = (card) => {
    if (showAllMode && card) {
      onChange({
        card,
        action: cardBuilderActions.CARD_ADDED,
      });
    } else {
      setShowCardOptions(true);
    }
  };

  const editCard = (card) => {
    onChange({
      card,
      action: cardBuilderActions.EDIT,
    });
  };

  const removeCard = (card) => {
    onChange({
      card,
      action: cardBuilderActions.CARD_REMOVED,
    });
  };

  const saveCard = () => {
    onChange({
      card: activeCard,
      action: cardBuilderActions.CARD_SAVED,
    });
    setShowCardOptions(false);
  };

  const onCloseModal = () => {
    onChange({
      card: activeCard,
      action: cardBuilderActions.MODAL_CLOSED
    });
    setShowCardOptions(false);
  };

  const contextValue = {
    builderKey: builderKey ? `${builderKey}.` : '',
    MessageComponent: MessageComponent || FormattedMessage
  };

  return <CardBuilderContext.Provider value={contextValue}>
    <div className={classes}>
      <CardsList
        {...rest}
        onAddClicked={addCard}
        onEditClicked={editCard}
        onRemoveClicked={removeCard}
        showAllMode={showAllMode}
        cards={cards}
        disabled={disabled}
      />
      {showModal ? (
        <CardModal
          {...rest}
          activeCard={activeCard}
          onClose={onCloseModal}
          onSave={saveCard}
        />
      ) : null}
    </div>
  </CardBuilderContext.Provider>;
};

const useCardBuilderContext =() => {
  return useContext(CardBuilderContext);
};

CardBuilder.propTypes = {
  activeCardId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  builderKey: PropTypes.string,
  cards: PropTypes.arrayOf(CardShape).isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  MessageComponent: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  renderCardBodyFn: PropTypes.func,
  renderModalHeaderFn: PropTypes.func,
  renderModalBodyFn: PropTypes.func,
  renderOptions: PropTypes.func,
  renderQuestion: PropTypes.func.isRequired,
  showAllMode: PropTypes.bool,
  values: PropTypes.array,
  theme: PropTypes.string,
};

export {
  CardBuilder as default,
  useCardBuilderContext
};
